import React, { useContext, useEffect, useState } from "react";
import {
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import ClearIcon from "@material-ui/icons/Clear";
import CheckIcon from "@material-ui/icons/Check";
import { TextField } from "@fluentui/react";
import { SubjectApi, SubjectDto } from "../../api-clients/api";
import { getApiUrl } from "../../api-clients";
import { AppContext } from "../../contexts/AppContext";
import Loading from "../Shared/Loading";
import NoResult from "../Shared/NoResult";
import FormButtons from "../Shared/FormButtons";
import { useTranslation } from "react-i18next";
import { useRole } from "../../hooks/useRole";

export default function SubjectsSettings() {
  const { t } = useTranslation();
  const { isAdmin } = useRole();
  const { openSnackbar } = useContext(AppContext);
  const [subjectApi] = useState<SubjectApi>(new SubjectApi(getApiUrl()));
  const [subjects, setSubjects] = useState<SubjectDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [newSubjectName, setNewSubjectName] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadSubjects = () => {
    setIsLoading(true);
    subjectApi
      .getAll()
      .then((res) => {
        setSubjects(res);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  };

  useEffect(() => {
    loadSubjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectApi]);

  const createSubject = () => {
    if (newSubjectName.trim().length === 0) return;
    setSaving(true);
    subjectApi
      .create({ name: newSubjectName.trim() } as SubjectDto)
      .then(() => {
        setSaving(false);
        setNewSubjectName("");
        openSnackbar(t("subjects.saveSuccess"), "success");
        loadSubjects();
      })
      .catch(() => {
        setSaving(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  const updateSubject = (subject: SubjectDto) => {
    if (editingName.trim().length === 0) return;
    setSaving(true);
    subjectApi
      .update({ ...subject, name: editingName.trim() } as SubjectDto)
      .then(() => {
        setSaving(false);
        setEditingId(null);
        openSnackbar(t("subjects.updateSuccess"), "success");
        loadSubjects();
      })
      .catch(() => {
        setSaving(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  const deleteSubject = (subjectId: string) => {
    setSaving(true);
    subjectApi
      .delete(subjectId)
      .then(() => {
        setSaving(false);
        setDeletingId(null);
        openSnackbar(t("subjects.deleteSuccess"), "success");
        loadSubjects();
      })
      .catch(() => {
        setSaving(false);
        openSnackbar(t("common.errorOccurred"), "error");
      });
  };

  return (
    <div>
      {isAdmin && (
        <Grid container spacing={1} alignItems="flex-end">
          <Grid item xs={8} sm={4}>
            <TextField
              label={t("subjects.name")}
              placeholder={t("subjects.namePlaceholder")}
              value={newSubjectName}
              onChange={(
                event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                newValue?: string
              ) => setNewSubjectName(newValue ?? "")}
            />
          </Grid>
          <Grid item xs={4} sm={2}>
            <FormButtons
              onConfirmClick={createSubject}
              disabled={saving || newSubjectName.trim().length === 0}
              cancelButtonVisible={false}
              confirmIcon="Add"
              confirmText={t("common.add")}
            />
          </Grid>
        </Grid>
      )}
      <div style={{ marginTop: "16px" }}>
        {!isLoading ? (
          subjects.length > 0 ? (
            <List>
              {subjects.map((s) => (
                <div key={s.subjectId}>
                  <ListItem>
                    {editingId === s.subjectId ? (
                      <TextField
                        autoFocus
                        value={editingName}
                        style={{ width: "100%" }}
                        onChange={(
                          event: React.FormEvent<
                            HTMLInputElement | HTMLTextAreaElement
                          >,
                          newValue?: string
                        ) => setEditingName(newValue ?? "")}
                      />
                    ) : (
                      <ListItemText primary={s.name} />
                    )}
                    <ListItemSecondaryAction>
                      {!isAdmin ? null : editingId === s.subjectId ? (
                        <>
                          <IconButton
                            aria-label={t("common.cancel")}
                            disabled={saving}
                            onClick={() => setEditingId(null)}
                          >
                            <ClearIcon />
                          </IconButton>
                          <IconButton
                            aria-label={t("common.confirm")}
                            disabled={saving}
                            onClick={() => updateSubject(s)}
                            color="secondary"
                          >
                            <CheckIcon />
                          </IconButton>
                        </>
                      ) : deletingId === s.subjectId ? (
                        <>
                          <IconButton
                            aria-label={t("common.cancel")}
                            disabled={saving}
                            onClick={() => setDeletingId(null)}
                          >
                            <ClearIcon />
                          </IconButton>
                          <IconButton
                            aria-label={t("common.confirm")}
                            disabled={saving}
                            onClick={() =>
                              s.subjectId && deleteSubject(s.subjectId)
                            }
                            color="secondary"
                          >
                            <CheckIcon />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            aria-label={t("common.edit")}
                            onClick={() => {
                              setEditingId(s.subjectId ?? null);
                              setEditingName(s.name ?? "");
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            aria-label={t("common.delete")}
                            onClick={() => setDeletingId(s.subjectId ?? null)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider />
                </div>
              ))}
            </List>
          ) : (
            <NoResult />
          )
        ) : (
          <Loading />
        )}
        {saving && <CircularProgress size={20} />}
      </div>
    </div>
  );
}
